import {
  Anchor,
  Button,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { NavBarContainer } from "./NavBarContainer";
import { getCurrentUser } from "../shared/getCurrentUser";
import { ApiError, RestApi } from "../shared/RestApi";
import { getSignInReturnPath, POST_SIGN_IN_PATH, postSignInDestination } from "../shared/postSignIn";

export function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const user = getCurrentUser();
  const requestedReturn = getSignInReturnPath(params.get("return"));
  const returnTo =
    user && requestedReturn === POST_SIGN_IN_PATH
      ? postSignInDestination(Boolean(user.Bought))
      : requestedReturn;

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (user) {
      navigate(returnTo, { replace: true });
    }
  }, [user, returnTo, navigate]);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await RestApi.localAuthRegister(email.trim(), password);
      window.location.href = returnTo;
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : t("registerAccount.error");
      setError(message);
      setSubmitting(false);
    }
  };

  return (
    <NavBarContainer>
      <Stack gap="md" maw={480}>
        <Title order={3}>{t("registerAccount.title")}</Title>
        <Text>{t("registerAccount.intro")}</Text>
        <form onSubmit={onSubmit}>
          <Stack gap="sm">
            <TextInput
              label={t("registerAccount.email")}
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
            <PasswordInput
              label={t("registerAccount.password")}
              description={t("registerAccount.passwordHint")}
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
            />
            {error ? (
              <Text c="red" size="sm">
                {error}
              </Text>
            ) : null}
            <Button type="submit" loading={submitting}>
              {t("registerAccount.submit")}
            </Button>
          </Stack>
        </form>
        <Text size="sm">
          {t("registerAccount.haveAccount")}{" "}
          <Anchor
            component={Link}
            to={`/signin?return=${encodeURIComponent(returnTo)}`}
          >
            {t("registerAccount.signIn")}
          </Anchor>
        </Text>
      </Stack>
    </NavBarContainer>
  );
}
